import { groupBy } from 'lodash';
import { useIntl } from 'react-intl';
import { Alert, Button, Card, CardBody, CardText, Container } from 'reactstrap';
import {
  TaskTemplateDependency,
  TaskTemplateLink as TaskTemplateLinkModel,
  useUnlinkTaskTemplateMutation,
} from '../../../core/api/rtk/taskTemplatesApi';

interface TaskTemplateLinkProps {
  task?: {
    id: number;
    linkedTaskTemplates?: TaskTemplateLinkModel[] | null;
  };
  readOnly?: boolean;
}

const DEPENDENCY_LABEL_IDS: Record<TaskTemplateDependency, string> = {
  BlockedBy: 'blockedBy',
  DoneBefore: 'doneBefore',
  DoneAfter: 'doneAfter',
  RelatesTo: 'relatesTo',
  SubTask: 'subTask',
};

export const TaskTemplateLink = ({
  task,
  readOnly = false,
}: TaskTemplateLinkProps): JSX.Element => {
  const intl = useIntl();
  const [unlinkTaskTemplate, { isLoading: isUnlinking }] = useUnlinkTaskTemplateMutation();
  const links = task?.linkedTaskTemplates ?? [];
  if (!task?.id || links.length === 0) {
    return <div>{intl.formatMessage({ id: 'noLinkedTasks' })}</div>;
  }
  const grouped = groupBy(links, 'dependency') as Record<string, TaskTemplateLinkModel[]>;
  const handleUnlink = async (linkId: number): Promise<void> => {
    await unlinkTaskTemplate({ taskId: task.id, linkId }).unwrap();
  };
  return (
    <div>
      {Object.entries(grouped).map(([dependency, group]) => {
        const labelId = DEPENDENCY_LABEL_IDS[dependency as TaskTemplateDependency] ?? dependency;
        return (
          <Container key={dependency} className="linked-tasks">
            <Alert className="mb-0" color="light">
              {intl.formatMessage({ id: labelId, defaultMessage: dependency })}
            </Alert>
            {group.map((link) => (
              <Card key={`linkedTaskId-${link.id}`} className="mb-3">
                <CardBody className="task-link-card-body">
                  <CardText>
                    {intl.formatMessage({ id: 'taskId', defaultMessage: 'Task Id' })}:{' '}
                    {link.linkedTaskId}
                  </CardText>
                  <div>{link.name}</div>
                  {link.status && <small className="text-muted">{link.status}</small>}
                </CardBody>
                {!readOnly && (
                  <CardBody className="pt-0">
                    <Button
                      color="danger"
                      size="sm"
                      disabled={isUnlinking}
                      onClick={() => handleUnlink(link.id)}
                    >
                      {intl.formatMessage({ id: 'removeLink', defaultMessage: 'Remove link' })}
                    </Button>
                  </CardBody>
                )}
              </Card>
            ))}
          </Container>
        );
      })}
    </div>
  );
};

export default TaskTemplateLink;
